import axiosInstance from "./AxiosInstance";

const API_URL = "/notifications";

// All keyed by the logged-in person's email (same convention as
// studentdashboard) plus which portal they're on (`recipientType`:
// "student" | "trainer" | "contactperson") — the backend resolves that pair
// to the actual recipient's own id, so the frontend never has to know or
// carry that id around.
export const getNotifications = async (email, recipientType = "student", page = 1, limit = 20) => {
  try {
    const response = await axiosInstance.get(API_URL, { params: { email, recipientType, page, limit } });
    return response.data.data; // { notifications, unreadCount }
  } catch (error) {
    console.error("Error fetching notifications", error);
    throw error;
  }
};

// Same endpoint as getNotifications, but keeps the `pagination` block that
// sits alongside `data` in the envelope — the Notifications page pages through
// the full history, where the bell only ever shows the latest 20.
export const getNotificationsPage = async (email, recipientType = "student", page = 1, limit = 20) => {
  try {
    const response = await axiosInstance.get(API_URL, { params: { email, recipientType, page, limit } });
    const { notifications = [], unreadCount = 0 } = response.data?.data || {};
    return { notifications, unreadCount, pagination: response.data?.pagination || null };
  } catch (error) {
    console.error("Error fetching notifications", error);
    throw error;
  }
};

// The newest few, for the bell to say what just arrived. silent: the bell
// asks this on its own when the count rises -- nobody pressed anything, so
// the app's loading veil must not appear (see Loader/busy.js).
export const getLatestNotifications = async (email, recipientType = "student", limit = 5) => {
  try {
    const response = await axiosInstance.get(API_URL, {
      params: { email, recipientType, page: 1, limit },
      silent: true,
    });
    return response.data?.data?.notifications || [];
  } catch (error) {
    console.error("Error fetching latest notifications", error);
    return [];
  }
};

export const deleteNotification = async (id) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${id}`);
    return response.data.data;
  } catch (error) {
    console.error("Error deleting notification", error);
    throw error;
  }
};

export const getUnreadNotificationCount = async (email, recipientType = "student") => {
  try {
    /* silent: nobody asked for this. The bell polls it every 60s, and a
       veil appearing over the app once a minute on its own is the
       interface seizing up for no reason the reader can see. See
       Loader/busy.js. */
    const response = await axiosInstance.get(`${API_URL}/unread-count`, {
      params: { email, recipientType },
      silent: true,
    });
    return response.data.data?.unreadCount || 0;
  } catch (error) {
    console.error("Error fetching unread notification count", error);
    return 0;
  }
};

export const markNotificationRead = async (id) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}/read`);
    return response.data.data;
  } catch (error) {
    console.error("Error marking notification read", error);
    throw error;
  }
};

export const markAllNotificationsRead = async (email, recipientType = "student") => {
  try {
    const response = await axiosInstance.put(`${API_URL}/read-all`, {}, { params: { email, recipientType } });
    return response.data.data;
  } catch (error) {
    console.error("Error marking all notifications read", error);
    throw error;
  }
};
